import ModalWrapper from "./modalWrapper";
import React, {ReactElement, useCallback, useEffect, useMemo, useState} from "react";
import {getQuizById} from "../../api/quizService";
import Quiz from "../../common/models/quiz/quiz.model";
import {QuizType} from "../../common/enums/quizType.enum";
import {Box, Stack, useTheme} from "@mui/material";
import {QuestionAnswering} from "@carbon/icons-react";
import {LoadingPageTitle} from "../../common/enums/loadingPageTitle.enum";
import {ModalWrapperProps} from "../../common/interfaces/modalWrapperProps.interface";
import QuizQuestionsStep from "../quiz/quizQuestionsStep";
import QuizResultsStep from "../quiz/quizResultsStep";
import QuizLoadingStep from "../quiz/quizLoadingStep";
import QuizModalHeader from "../quiz/quizModalHeader";
import StarSign from "../../common/icons/brand/starSign";
import Personality from "../../common/icons/brand/personality";
import LoveHeart from "../../common/icons/brand/loveHeart";
import CategoryAnswer from "../../common/models/answer/categoryAnswer.model";
import {toast} from "react-toastify";
import {useIsMobile} from "../../common/hooks/useIsMobile";

interface Props extends Omit<ModalWrapperProps, "children" | "onClose" | "onBackgroundTransitionEnd"> {
    quizId: number;
    onComplete: (categoryAnswers: CategoryAnswer[]) => void;
    onClose: () => void;
}

interface QuizDefaults {
    icon: (fillColor?: string) => ReactElement;
    loadingTitle: LoadingPageTitle;
    hasProgress: boolean;
    progressColor?: string;
    resultsBackgroundColor?: string;
    resultsIconColor?: string;
}

export const QuizModal: React.FC<Props> = ({ quizId, openModal, onClose, onComplete }) => {

    const theme = useTheme();
    const isMobile = useIsMobile();
    const [quiz, setQuiz] = useState<Quiz>();
    const [currentStep, setCurrentStep] = useState(1);
    const [progress, setProgress] = useState<number>(0);
    const [modalBackgroundColor, setModalBackgroundColor] = useState<string|undefined>(theme.palette.thatsMe.surfacePrimary);
    const [modalHeight, setModalHeight] = useState<string>();
    const [modalWidth, setModalWidth] = useState<string>();
    const [animateBackground, setAnimateBackground] = useState(false);
    const memoizedQuizDefaults = useMemo(() => { return {
        icon: (fillColor) => <QuestionAnswering style={{ marginBottom: 2}} />,
        loadingTitle: LoadingPageTitle.DEFAULT,
        resultsBackgroundColor: theme.palette.thatsMe.surfacePrimary!,
        hasProgress: false,
        progressColor: undefined
    } as QuizDefaults;}, []);
    const [quizDefaults, setQuizDefaults] = useState<QuizDefaults>(memoizedQuizDefaults);
    const [quizResults, setQuizResults] = useState<CategoryAnswer[]|undefined>();
    const [backgroundAnimationEnded, setBackgroundAnimationEnded] = useState(false);
    const duration = 300;
    const transitionBaseStyling = useMemo(() => { return { transition: theme.transitions.create(['opacity'], { duration, easing: 'linear'}), opacity: 0 }; }, []);
    const transitionTransitionedStyling = useMemo(() => { return { transition: theme.transitions.create(['opacity'], { duration, easing: 'linear'}), opacity: 1 }; }, []);
    const [fadeIn, setFadeIn] = useState(false);
    const modalHeights = useMemo(() => {
        return {
            [QuizType.MBTI]: 680,
            [QuizType.LoveLanguage]: 500,
            default: 600
        }
    }, []);

    useEffect(() => {
        
        if(!quizId) {
            resetModalToFormStep();
            return;
        }

        async function getQuiz() {
            const response = await getQuizById(quizId);
            if(response)
            {
                assignQuizDefaults(response);
                setQuiz(response);
                setFadeIn(true);
            }
        }

        void getQuiz();

        return () => {
            resetModalToFormStep();
            setQuiz(undefined);
            setQuizDefaults(memoizedQuizDefaults);
        };
    }, [quizId]);
    
    useEffect(() => {
        if(!quiz) {
            return;
        }

        const handleHeightByTheme = () => {
            switch (quiz.quizType) {
                case QuizType.MBTI:
                    setModalHeight(isMobile ? undefined : `${modalHeights[QuizType.MBTI]}px`);
                    break;
                case QuizType.LoveLanguage:
                    setModalHeight(isMobile ? undefined : `${modalHeights[QuizType.LoveLanguage]}px`);
                    break;
                default:
                    setModalHeight(isMobile ? undefined : `${modalHeights.default}px`);
                    break;
            }
        }
        assignQuizDefaults(quiz);
        setModalWidth(isMobile ? undefined : "500px");
        void handleHeightByTheme();

        const handleResize = () => {

            const heightComparison = quiz.quizType === QuizType.MBTI ? modalHeights[QuizType.MBTI] : modalHeights.default;

            if(window.innerHeight < heightComparison) {
                setModalHeight(undefined);
                return;
            }

            void handleHeightByTheme();
        }

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [isMobile, quiz]);

    useEffect(() => {
        setBackgroundAnimationEnded(false);
        setTimeout(() => setFadeIn(true), duration);
    }, [currentStep]);

    const resetModalToFormStep = () => {
        setProgress(0);
        setCurrentStep(1);
        setModalBackgroundColor(theme.palette.thatsMe.surfacePrimary);
        setQuizResults(undefined);
    };

    const assignQuizDefaults = (quiz: Quiz) => {
        let defaults: QuizDefaults;

        switch (quiz.quizType) {
            case QuizType.StarSign: {
                defaults = {
                    icon: (fillColor) => <StarSign
                        fill={fillColor ?? theme.palette.thatsMe.iconStar}
                        size={26}
                    />,
                    loadingTitle: LoadingPageTitle.STARSIGN,
                    hasProgress: false
                };
                
                if(theme.palette.thatsMe.quiz) {
                    defaults.resultsBackgroundColor = theme.palette.thatsMe.quiz?.starSign.resultsBackground;
                    defaults.resultsIconColor = theme.palette.thatsMe.quiz?.starSign.resultsIconColor;
                }

                break;
            }
            case QuizType.MBTI: {
                defaults = {
                    icon: (fillColor) => <Personality
                        fill={fillColor ?? theme.palette.thatsMe.iconEclipse}
                        size={26}
                    />,
                    loadingTitle: LoadingPageTitle.MBTI,
                    hasProgress: true
                };

                if(theme.palette.thatsMe.quiz) {
                    defaults.progressColor = theme.palette.thatsMe.quiz?.mbti.resultsBackground;
                    defaults.resultsBackgroundColor = theme.palette.thatsMe.quiz?.mbti.resultsBackground;
                    defaults.resultsIconColor = theme.palette.thatsMe.quiz?.mbti.resultsIconColor;
                }

                break;
            }
            case QuizType.LoveLanguage: {
                defaults = {
                    icon: (fillColor) => <Box pt={isMobile ? 8 : undefined}>
                        <LoveHeart
                            fill={fillColor ?? theme.palette.thatsMe.iconHeart}
                            size={26}
                        />
                    </Box>,
                    loadingTitle: LoadingPageTitle.LOVELANGUAGE,
                    hasProgress: false
                };

                if(theme.palette.thatsMe.quiz) {
                    defaults.resultsBackgroundColor = theme.palette.thatsMe.quiz?.loveLanguage.resultsBackground;
                    defaults.resultsIconColor = theme.palette.thatsMe.quiz?.loveLanguage.resultsIconColor;
                }

                break;
            }
            default: {
                return;
            }
        }

        setQuizDefaults(defaults);
    };
    
    const onProgressChanged = useCallback((value: number) => {
        setProgress(value);
    }, []);

    const onCalculatingQuizResult = useCallback(() => {
        setFadeIn(false);
        setTimeout(() => {
            setCurrentStep(step => ++step);
        }, duration);
    }, []);

    const onCalculatedQuizResult = useCallback((categoryAnswers: CategoryAnswer[]) => {
        setQuizResults(categoryAnswers);
        setTimeout(() => {
            setModalBackgroundColor(quizDefaults.resultsBackgroundColor);
            setAnimateBackground(true);
            setCurrentStep(step => ++step);
        }, duration);
    },[quizDefaults]);

    const onFillResultsClick = () => {
        
        if(!quizResults)
        {
            toast.error("Error setting quiz results to profile. Please try again.");
            return;
        }

        onComplete(quizResults);

        setTimeout(resetModalToFormStep, 500); //avoids flicker
    };

    const onInternalClose = () => {
        onClose();
        setTimeout(resetModalToFormStep, 500); //avoids flicker
    };
    
    const getHeader = useMemo(() => {
        if(!quiz) {
            return <></>;
        }
        
        if(currentStep === 1) {
            return <QuizModalHeader
                title={quiz.title}
                subtitle={quiz.subtitle}
                icon={quizDefaults.icon()}
            />; 
        }

        return <QuizModalHeader
            title={quizDefaults.loadingTitle}
            icon={quizDefaults.icon(currentStep === 2 || (currentStep === 3 && !backgroundAnimationEnded) ? undefined : quizDefaults.resultsIconColor)}
        />;
    }, [quiz, currentStep, backgroundAnimationEnded, quizDefaults])
    
    const onModalBackgroundTransitionEnd = () => {
        setBackgroundAnimationEnded(true);
    };

    return <ModalWrapper
        openModal={openModal}
        onClose={onInternalClose}
        color={modalBackgroundColor}
        max-width={{xs: 600, sm: 500}}
        hasProgress={quizDefaults.hasProgress}
        progress={progress}
        progressColor={quizDefaults.progressColor}
        modalFixedHeight={modalHeight}
        modalFixedWidth={modalWidth}
        onBackgroundTransitionEnd={onModalBackgroundTransitionEnd}
        animateBackground={animateBackground}
    >
        <Stack
            style={quiz && fadeIn ? transitionTransitionedStyling : transitionBaseStyling}
            flex={1}
        >
            {getHeader}
            {
                quiz && currentStep === 1 &&
                    <QuizQuestionsStep
                        quiz={quiz}
                        onProgressChanged={onProgressChanged}
                        onCalculatingQuizResult={onCalculatingQuizResult}
                        onCalculatedQuizResult={onCalculatedQuizResult}
                    />
            }
            {
                quiz && currentStep === 2 && <QuizLoadingStep variant={(quiz.quizType as string).toLowerCase()}/>
            }
            {
                currentStep === 3 && 
                <QuizResultsStep
                    onFillResultsClick={onFillResultsClick}
                    results={quizResults}
                    quizType={quiz?.quizType}
                    animateInResults={backgroundAnimationEnded}
                />
            }
        </Stack>
    </ModalWrapper>;
};